import { createAppRequest } from '@/services/request';

const request = createAppRequest();
const base = '/bff/cms/site';

export type SiteType = 'corporate' | 'brand' | 'product' | 'topic' | 'campaign';
export type SiteStatus = 'preparing' | 'active' | 'maintenance' | 'disabled';

export interface ManagedSite {
  uuid: string;
  name: string;
  brandUuid: string;
  productUuid: string | null;
  ownerMemberUuid: string | null;
  domain: string;
  siteType: SiteType;
  status: SiteStatus;
  description: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SiteOverview {
  site: ManagedSite;
  briefCompletion: number;
  pageCount: number;
  publishedPageCount: number;
  mappingCount: number;
  contentCount: number;
  pendingPublicationCount: number;
  externalMetrics: { status: 'not_connected' };
  sources: {
    operation: 'not_connected';
    seo: 'not_connected';
    lead: 'not_connected';
    analytics: 'not_connected';
  };
}

export interface SiteContentRelation {
  uuid: string;
  siteUuid: string;
  contentUuid: string;
  createdAt: string;
  updatedAt: string;
}

export interface PageResult<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface BusinessOption {
  uuid: string;
  name: string;
  entityType: string;
}

export interface MemberOption {
  id: string;
  memberUuid: string;
  name?: string;
  nickname?: string;
  username?: string;
}

export type SiteAnalyticsProvider = 'baidu' | 'umeng-cnzz' | '51la';

export interface SiteAnalyticsConfig {
  provider: SiteAnalyticsProvider | null;
  code: string | null;
  scripts?: Record<string, string>;
}

export type SiteSettingsGroup = 'website' | 'sitemap' | 'robots' | 'basic' | 'rss' | 'verification' | 'seo' | 'smtp' | 'media' | 'security';
export interface SitePageLink { key: string; label: string; href: string; included: boolean; changeFrequency: string; priority: number }
export interface SiteRobotsRule { userAgent: string; allow: string[]; disallow: string[] }
export type SiteSettingsValues = Record<string, string | number | boolean | string[] | SitePageLink[] | SiteRobotsRule[]>;

export const siteManagementApi = {
  list: (params: Record<string, unknown>) =>
    request<PageResult<ManagedSite>>(base, { method: 'GET', params }),
  get: (uuid: string) => request<ManagedSite>(`${base}/${uuid}`),
  overview: (uuid: string) => request<SiteOverview>(`${base}/${uuid}/overview`),
  create: (data: Partial<ManagedSite>) =>
    request<ManagedSite>(base, { method: 'POST', data }),
  update: (uuid: string, data: Partial<ManagedSite>) =>
    request<ManagedSite>(`${base}/${uuid}`, { method: 'PATCH', data }),
  delete: (uuid: string, reason?: string) =>
    request<void>(`${base}/${uuid}`, {
      method: 'DELETE',
      params: reason ? { reason } : {},
    }),
  contentRelations: (uuid: string) =>
    request<SiteContentRelation[]>(`${base}/${uuid}/content-relation`),
  attachContent: (uuid: string, contentUuid: string) =>
    request<SiteContentRelation>(`${base}/${uuid}/content-relation`, {
      method: 'POST',
      data: { contentUuid },
    }),
  detachContent: (uuid: string, contentUuid: string) =>
    request<void>(`${base}/${uuid}/content-relation/${encodeURIComponent(contentUuid)}`, {
      method: 'DELETE',
    }),
  businessOptions: () =>
    request<{
      brands: BusinessOption[];
      products: BusinessOption[];
      members: MemberOption[];
    }>(`${base}/business-options`),
  analytics: (uuid: string) =>
    request<SiteAnalyticsConfig>(`${base}/${uuid}/analytics`),
  saveAnalytics: (
    uuid: string,
    data: {
      provider?: SiteAnalyticsProvider | null;
      code?: string | null;
      scripts?: Record<string, string>;
    },
  ) => request<SiteAnalyticsConfig>(`${base}/${uuid}/analytics`, { method: 'PUT', data }),
  settings: (uuid: string, group: SiteSettingsGroup) =>
    request<SiteSettingsValues & { passwordConfigured?: boolean }>(`${base}/${uuid}/settings/${group}`),
  saveSettings: (uuid: string, group: SiteSettingsGroup, settings: SiteSettingsValues) =>
    request<SiteSettingsValues & { passwordConfigured?: boolean }>(`${base}/${uuid}/settings/${group}`, { method: 'PUT', data: { settings } }),
  testSmtp: (uuid: string, toEmail: string) =>
    request<{ sent: boolean; toEmail: string }>(`${base}/${uuid}/settings/smtp/test`, { method: 'POST', data: { toEmail } }),
  previewRobots: (uuid: string) =>
    request<RobotsPreviewResult>(`${base}/${uuid}/settings/robots/preview`),
  previewSitemap: (uuid: string) =>
    request<SitemapPreviewResult>(`${base}/${uuid}/settings/sitemap/preview`),
  validateOnlineRobots: (uuid: string) =>
    request<OnlineProbeResult>(`${base}/${uuid}/settings/robots/validate-online`, { method: 'POST' }),
  validateOnlineSitemap: (uuid: string) =>
    request<OnlineProbeResult>(`${base}/${uuid}/settings/sitemap/validate-online`, { method: 'POST' }),
  validateOnlineIndexing: (uuid: string, path?: string) =>
    request<OnlineProbeResult>(`${base}/${uuid}/settings/indexing/validate-online`, { method: 'POST', data: { path } }),
};

export interface RobotsPreviewResult {
  content: string;
  robotsIndex: boolean;
  sitemapEnabled: boolean;
  rulesCount: number;
  sitemapUrl: string | null;
  origin: string | null;
}

export interface SitemapPreviewResult {
  content: string;
  totalUrls: number;
  pagesCount: number;
  articlesCount: number;
  robotsIndex: boolean;
  sitemapEnabled: boolean;
  origin: string | null;
}

export interface OnlineProbeResult {
  status: 'success' | 'failed' | 'unreachable';
  httpStatus?: number | null;
  url: string | null;
  message: string;
  content?: string;
  urlCount?: number;
  isConsistent?: boolean;
  expectedIndex?: boolean;
  actualXRobotsTag?: string | null;
  actualMetaRobots?: string | null;
  issues?: string[];
}
